import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatBytes } from '../utils/navigateUtils';
import { Description, Download, ImageSearch, Close } from '@mui/icons-material';
import { Box, ListItemText, Button } from '@mui/material';
import styled from '@emotion/styled';
import ImageViewer from './ImageViewer';
import { getApiUrl } from '../config';
import { useIsLocked } from '../utils/lockUtils';
import { isRasterImage } from '../utils/uploadUtils';

const AttachmentContainer = styled(Box, { shouldForwardProp: (prop) => prop !== "isDeleted" })(({ theme, isDeleted }) => ({
    display: 'flex',
    backgroundColor: isDeleted
        ? theme.palette.action.disabled
        : theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1),
    border: `1px solid ${theme.palette.text.disabled}`,
    margin: theme.spacing(2, 0),
    alignItems: 'center',
    textDecoration: isDeleted ? 'line-through' : 'none',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    gap: theme.spacing(1),
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0.5, 1),
    }
}));

const AttachmentName = styled(ListItemText)(({ theme }) => ({
    margin: theme.spacing(0, 0, 0, 1),
    padding: theme.spacing(0),
    flex: 1,
    minWidth: 0,
    '& .MuiListItemText-primary': {
        fontWeight: theme.typography.fontWeightMedium,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    '& .MuiListItemText-secondary': {
        fontSize: theme.typography.caption.fontSize,
        color: theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    cursor: 'pointer',
    '&:hover': {
        '& .MuiListItemText-primary': {
            color: theme.palette.primary.dark,
            textDecoration: 'underline',
        }
    },
    [theme.breakpoints.down('md')]: {
        maxWidth: 'calc(100% - 60px)', // Reserve space for the button
    }
}));

const DownloadButton = styled(Button)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[0],
    '&:hover': {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        boxShadow: theme.shadows[2],
    },
}));

const RemoveButton = styled(Button)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    minWidth: 0,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    boxShadow: theme.shadows[0],
    '&:hover': {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.contrastText,
        boxShadow: theme.shadows[2],
    },
}));


const AttachmentBox = React.memo(({ attachment, onReupload, onRemove }) => {
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const projectAccount = useSelector(state => state.project?.currentProject?.account);
    const isLocked = useIsLocked(projectAccount, projectAccount?.client);

    const handleAttachmentClick = useCallback((attachment) => {
        if (isLocked) return;
        const isImage = isRasterImage(attachment.mimeType);

        if (isImage) {
            setImageViewerOpen(true);
        } else {
            const attachmentUrl = `${getApiUrl()}/attachment/${attachment._id}`;
            const link = document.createElement('a');
            link.href = attachmentUrl;
            link.download = attachment.name;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }, [isLocked]);

    const handleDownloadFromViewer = useCallback((attachment) => {
        if (isLocked) return;
        const attachmentUrl = `${getApiUrl()}/attachment/${attachment._id}`;
        const link = document.createElement('a');
        link.href = attachmentUrl;
        link.download = attachment.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [isLocked]);

    const handleCloseImageViewer = useCallback(() => {
        setImageViewerOpen(false);
    }, []);

    const isDownloadable = !onRemove && !!attachment._id;

    return (
        <>
            <AttachmentContainer key={attachment._id} isDeleted={attachment.isDeleted}>
                <Description />
                <AttachmentName primary={attachment.name} secondary={`Size: ${formatBytes(attachment.size)}`} onClick={isDownloadable ? () => handleAttachmentClick(attachment) : undefined}/>
                {onRemove ? (
                    <RemoveButton size="small" aria-label="Remove attachment" onClick={() => onRemove(attachment)}>
                        <Close />
                    </RemoveButton>
                ) : !attachment.isDeleted && (
                    <DownloadButton size="small" onClick={() => handleAttachmentClick(attachment)} disabled={isLocked}>
                        {isRasterImage(attachment.mimeType) ? <ImageSearch /> : <Download />}
                    </DownloadButton>
                )}
            </AttachmentContainer>

            {!onRemove && (
                <ImageViewer open={imageViewerOpen} onClose={handleCloseImageViewer} attachment={attachment} onDownload={handleDownloadFromViewer} onReupload={onReupload} />
            )}
        </>
    );
});

export default AttachmentBox;